import {
  BlockTitle,
  Button,
  CopyField,
  EmptyState,
  PageHeader,
  PageTitle,
  RowActions,
  StatusTag,
  Surface,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  toast,
} from '@westhetics/ui'
import { Link, useNavigate } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { routes } from '@/routes'
import { useIntegrations, useIntegrationsCatalog, type Integration } from './api'
import { AssistantGuide } from './assistant-guide'
import {
  API_URL,
  DOCS_URL,
  DOCS_WEBHOOKS_URL,
  integrationStatus,
  keyHint,
  lastUsedText,
  scopeSummary,
} from './labels'

/**
 * Integrações (docs/areas/integracoes.md, "As telas"; RN-INT-01, P-INT-08). Arquétipo: lista de trabalho, da
 * gestora e de root com clínica ativa. A pendência é a entrega de webhook que falhou: o sistema da clínica deixou
 * de receber o que aconteceu. Abaixo, as duas outras portas: o assistente de IA (MCP) e a API para quem desenvolve.
 */

const linkClass = 'text-ink border-hairline-heavy hover:border-ink border-b'

function WebhooksCell({ integration }: { integration: Integration }) {
  const { active } = integration.webhooks
  const { failed } = integration.deliveries
  if (integration.webhooks.total === 0) return <span className="text-secondary">nenhum</span>
  return (
    <span>
      {active} {active === 1 ? 'ativo' : 'ativos'}
      {failed > 0 ? (
        <span className="text-plum">
          {' · '}
          {failed} {failed === 1 ? 'entrega com falha' : 'entregas com falha'}
        </span>
      ) : null}
    </span>
  )
}

export function IntegrationsPage() {
  const navigate = useNavigate()
  const query = useIntegrations()
  const catalog = useIntegrationsCatalog()
  const items = query.data?.data ?? []
  const meta = query.data?.meta
  const atLimit = meta ? items.length >= meta.limit : false
  const failing = items.filter((i) => i.deliveries.failed > 0).length

  const newApp = atLimit ? (
    <Button size="sm" disabled>
      Limite de {meta?.limit} apps ativos
    </Button>
  ) : (
    <Button size="sm" asChild>
      <Link to={routes.admin.newIntegration}>+ Novo app</Link>
    </Button>
  )

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Administração' }, { label: 'Integrações' }]}
        actions={newApp}
      />
      <Page wide>
        <PageTitle
          title="Integrações"
          subtitle={
            meta && items.length > 0
              ? `${items.length} ${items.length === 1 ? 'app ativo' : 'apps ativos'} de ${meta.limit}${
                  failing > 0 ? ` · ${failing} com entrega falhando` : ''
                } · em ordem alfabética`
              : 'Conecte a clínica ao CRM, ao WhatsApp, à agenda e ao assistente de IA, sem digitar nada duas vezes.'
          }
        />

        <section className="mt-8" aria-label="Apps">
          <BlockTitle title="Apps" />
          <div className="mt-3">
            {query.isError ? (
              <LoadError
                title="Não conseguimos carregar os apps"
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            ) : query.isPending ? (
              <TableSkeleton rows={3} columns={6} />
            ) : items.length === 0 ? (
              <EmptyState
                title="Nenhum app conectado ainda"
                description="Um app lê e escreve na clínica pela API, com a chave que você cria aqui e só com as permissões que você der: o CRM cadastra a paciente, a automação manda a anamnese, o sistema financeiro sabe quando o contrato foi assinado."
                action={
                  <Button asChild>
                    <Link to={routes.admin.newIntegration}>Criar o primeiro app</Link>
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <tr>
                    <TableHead>App</TableHead>
                    <TableHead>Permissões</TableHead>
                    <TableHead>Webhooks</TableHead>
                    <TableHead>Uso</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead aria-label="Ações" />
                  </tr>
                </TableHeader>
                <TableBody>
                  {items.map((integration) => {
                    const status = integrationStatus(integration)
                    return (
                      <TableRow
                        key={integration.id}
                        onClick={() => navigate(routes.admin.integration(integration.id))}
                      >
                        <TableCell primary>
                          <span className="block">{integration.name}</span>
                          <span className="font-body text-secondary block text-[12px] leading-[1.6] font-light">
                            {keyHint(integration.keyPrefix)}
                          </span>
                        </TableCell>
                        <TableCell label="Permissões" muted>
                          {scopeSummary(integration.scopes, catalog.data)}
                        </TableCell>
                        <TableCell label="Webhooks" muted>
                          <WebhooksCell integration={integration} />
                        </TableCell>
                        <TableCell label="Uso" muted>
                          {lastUsedText(integration.lastUsedAt)}
                        </TableCell>
                        <TableCell label="Status">
                          <StatusTag level={status.level}>{status.label}</StatusTag>
                        </TableCell>
                        <TableCell>
                          <RowActions>
                            <Link to={routes.admin.integration(integration.id)}>abrir</Link>
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
            {meta && items.length > 0 ? (
              <TableFooter>
                <span>
                  {items.length} de {meta.limit} apps ativos
                  {meta.revokedCount > 0
                    ? ` · ${meta.revokedCount} ${meta.revokedCount === 1 ? 'revogado fica' : 'revogados ficam'} só na trilha`
                    : ''}
                </span>
              </TableFooter>
            ) : null}
          </div>
        </section>

        <div className="desk:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] mt-10 grid grid-cols-1 gap-6">
          <Surface role="region" aria-labelledby="assistants-title" className="max-desk:px-4">
            <h2 id="assistants-title" className="font-display text-block text-ink font-light">
              Assistentes de IA
            </h2>
            <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
              Claude, ChatGPT, Claude Code, Codex e Cursor, pelo MCP da Westhetics.
            </p>
            <div className="mt-5">
              <AssistantGuide withKey />
            </div>
          </Surface>

          <Surface
            role="region"
            aria-labelledby="developers-title"
            className="max-desk:px-4 self-start"
          >
            <h2 id="developers-title" className="font-display text-block text-ink font-light">
              Para quem desenvolve
            </h2>
            <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
              A API pública v1, em JSON, com a chave de um app ou o OAuth da pessoa.
            </p>
            <div className="mt-5 flex flex-col gap-5">
              <CopyField
                label="Endereço da API"
                value={API_URL}
                copyAriaLabel="Copiar o endereço da API"
                onCopied={() => toast('Endereço copiado')}
                note="Pacientes, anamneses, contratos e assinaturas, eventos e webhooks, com as mesmas regras da tela."
              />
              <div>
                <div className="eyebrow">Webhooks</div>
                <p className="font-body text-body text-ink mt-2 leading-[1.7] font-light">
                  Cada app pode avisar até 5 endereços do seu sistema quando algo acontece: paciente
                  cadastrada, anamnese respondida, contrato assinado. A entrega é assinada e tenta
                  de novo por até 42 horas.
                </p>
              </div>
              <ul className="font-body text-body flex flex-col gap-2 leading-[1.7] font-light">
                <li>
                  <a href={DOCS_URL} target="_blank" rel="noreferrer" className={linkClass}>
                    Documentação e referência da API
                  </a>
                </li>
                <li>
                  <a
                    href={DOCS_WEBHOOKS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={linkClass}
                  >
                    Eventos e como conferir a assinatura do webhook
                  </a>
                </li>
              </ul>
            </div>
          </Surface>
        </div>
      </Page>
    </>
  )
}
